<template>
  <div>
    <div>
      <p class="text-sm">
        Renseignez les coordonnées des utilisateurs qui seront invités à collaborer avec vous. Consultez le détail des
        rôles ici.
      </p>
      <Checkbox
        class="my-2"
        id="noSellerEmail"
        name="noSellerEmail"
        :label="t('no_seller_email_label')"
        labelClass="font-normal"
        v-model="noSellerEmail"
      />
      <template v-if="!noSellerEmail">
        <FormField
          id="email"
          label="Adresse email magasin"
          labelClass="font-normal"
          name="email"
          placeholder="abc@store.com"
          required
          v-model="formData.email"
        />
        <p class="mt-2 text-xs text-gray-600">Renseignez l'adresse email à laquelle vos vendeurs ont accès.</p>
      </template>
      <hr class="my-4" />
      <h3 class="font-bold pb-2">Coordonnées du référent du magasin</h3>
      <p class="text-sm">Le référent assurance est le responsable assurance du magasin.</p>
      <p class="text-sm">Il constitue le point de contact principal pour la formation et l’accompagnement.</p>
      <Checkbox
        class="my-2"
        id="current_profile_is_referent"
        name="current_profile_is_referent"
        label="Cochez cette case uniquement si vous êtes le référent du magasin"
        labelClass="font-normal"
        v-model="currentUserIsReferent"
      />
      <FormField
        id="referent_last_name"
        label="Nom"
        labelClass="font-normal"
        name="referent_last_name"
        required
        v-model="formData.referent.lastName"
      />
      <FormField
        id="referent_first_name"
        label="Prénom"
        labelClass="font-normal"
        name="referent_first_name"
        required
        v-model="formData.referent.firstName"
      />
      <FormField
        id="referent_email"
        label="Email"
        labelClass="font-normal"
        name="referent_email"
        placeholder="Email du manager"
        required
        v-model="formData.referent.email"
      />
      <FormField
        id="referent_phone"
        label="Numéro de téléphone"
        labelClass="font-normal"
        name="referent_phone"
        placeholder="06 12 34 56 78"
        required
        v-model="formData.referent.phone"
      />
      <Selector
        class="mt-2"
        id="referent_role"
        label="Fonction du référent"
        labelClass="font-normal"
        name="referent_role"
        required
        v-model="formData.referentRole"
        :options="referentRoles"
      />
    </div>
    <div class="mt-4 flex justify-end">
      <Loading v-if="isLoading" />
      <GenericButton v-else @click="updateContactInformations">
        <span v-if="store[configStatus] == 'pending_validation'">Modifier</span>
        <span v-else>Valider</span>
      </GenericButton>
    </div>
  </div>
</template>

<script>
import { Selector, Loading, Checkbox, GenericButton, FormField } from "@estaly/ui";
import StoresService from "@/api/services/stores";
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  props: ["store", "configStatus"],

  components: {
    GenericButton,
    Loading,
    Selector,
    Checkbox,
    FormField,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      isLoading: false,
      currentUserIsReferent: false,
      noSellerEmail: false,
      formData: {
        email: "",
        referent: {
          lastName: "",
          firstName: "",
          email: "",
          phone: "",
        },
        referentRole: "",
      },
      referentRoles: [],
    };
  },

  created() {
    this.setupFormData();
  },

  emits: ["configurationUpdated"],

  computed: {
    ...mapState("auth", ["currentUser"]),
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("auth", ["refreshCurrentUser"]),
    async setupFormData() {
      this.isLoading = true;
      const response = await StoresService.getContactInformations(this.store.id);
      if (response?.success) {
        const {
          data: { store, referent_roles },
        } = response;
        this.noSellerEmail = false;
        this.formData.email = store.email;
        this.formData.referent.lastName = store.referent.last_name;
        this.formData.referent.firstName = store.referent.first_name;
        this.formData.referent.email = store.referent.email;
        this.formData.referent.phone = store.referent.phone;
        this.formData.referentRole = store.referent_role;
        this.referentRoles = referent_roles;
      }
      this.isLoading = false;
    },
    async updateContactInformations() {
      if (!this.noSellerEmail && !this.formData.email) {
        this.notify({
          category: "simple",
          type: "error",
          title: "Adresse email vendeur",
          text: "Saisissez l'email vendeur ou cochez la case pour indiquer qu'il n'y en a pas.",
        });
        return;
      }
      const storeId = this.store.id;
      const email = this.noSellerEmail ? null : this.formData.email;
      const response = await StoresService.updateContactInformation(storeId, {
        store: {
          email,
          referent_role: this.formData.referentRole,
          referent: {
            last_name: this.formData.referent.lastName,
            first_name: this.formData.referent.firstName,
            email: this.formData.referent.email,
            phone: this.formData.referent.phone,
          },
        },
      });
      if (!response?.success) return;

      this.$emit("configurationUpdated");
      this.$emit("stepValidated");
      this.notify({
        category: "simple",
        type: "success",
        title: "Informations mises à jour",
        text: "Les coordonées du magasin ont été mises à jour avec succès",
      });
    },
  },
  watch: {
    noSellerEmail(checked) {
      if (checked) {
        this.formData.email = "";
      }
    },
    currentUserIsReferent(newValue) {
      if (newValue === true) {
        this.formData.referent.email = this.currentUser.email;
        this.formData.referent.firstName = this.currentUser.firstName;
        this.formData.referent.lastName = this.currentUser.lastName;
        this.formData.referent.phone = this.currentUser.phone;
      } else {
        this.formData.referent.email = "";
        this.formData.referent.firstName = "";
        this.formData.referent.lastName = "";
        this.formData.referent.phone = "";
      }
    },
    "store.id": {
      immediate: false,
      async handler() {
        this.noSellerEmail = false;
        this.formData = {
          email: "",
          currentUserIsReferent: false,
          referent: {
            lastName: "",
            firstName: "",
            email: "",
            phone: "",
          },
          referentRole: "",
        };
        this.referentRoles = [];
        await this.setupFormData();
      },
    },
  },
};
</script>

<i18n src="./ContactInformations.i18n.json"></i18n>
